<script>
import DatePicker from 'vue2-datepicker'
import {
  KEYCODE,
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  MONTH_TIME_FORMAT,
} from '@utils/constant'
import formatDate, {
  setHourAndMinute,
  getHoursOfDate,
  getMinutesOfDate,
  checkIsValidDate,
  checkDateFormat,
} from '@utils/format-date'
import 'vue2-datepicker/locale/ja'

export default {
  components: { DatePicker },
  props: {
    format: {
      type: String,
      default: 'YYYY-MM-DD',
      require: true,
    },
    type: {
      type: String,
      default: '',
      require: false,
    },
    initDate: {
      type: String,
      default: '',
      require: false,
    },
    rules: {
      type: Object,
      default: () => {},
      require: false,
    },
    disabled: {
      type: Boolean,
    },
    hideClear: {
      type: Boolean,
      default: false,
    },
    hideToday: {
      type: Boolean,
      default: false,
    },
    readonlyInput: {
      type: Boolean,
      default: false,
    },
    disabledBefore: {
      type: Date,
      default: null,
    },
    disabledAfter: {
      type: Date,
      default: null,
    },
    veeName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      date: null,
      hours: null,
      minutes: null,
      fullDate: null,
      oldFullDate: null,
      oldHours: null,
      oldMinutes: null,
      top: null,
    }
  },
  computed: {
    formatComputed() {
      if (this.type === 'datetime') {
        return DATE_TIME_FORMAT
      } else if (this.type === 'month') {
        return MONTH_TIME_FORMAT
      } else {
        return DATE_FORMAT
      }
    },
  },
  watch: {
    fullDate() {
      if (this.fullDate === this.oldFullDate) {
        return
      }
      this.oldFullDate = this.fullDate
      if (!this.fullDate) {
        this.$emit('update:date', null)
      } else {
        this.$emit('update:date', this.fullDate)
      }
      this.triggerUpdateDate(this.fullDate)
    },
    initDate() {
      let length = 0
      if (this.type === 'datetime') {
        length = 16
      } else if (this.type === 'month') {
        length = 7
      } else {
        length = 10
      }
      if (this.initDate === null || this.initDate === '') {
        this.handleFormatDate(null)
      } else if (
        this.initDate &&
        checkIsValidDate(this.initDate) &&
        this.initDate.length === length
      ) {
        if (this.initDate !== this.fullDate) {
          this.handleFormatDate(new Date(this.initDate))
        }
      }
    },
  },
  created() {
    if (this.initDate && checkIsValidDate(this.initDate)) {
      this.handleFormatDate(new Date(this.initDate))
    } else {
      this.handleFormatDate(null)
    }
  },
  methods: {
    triggerUpdateDate(date) {
      if (this.$refs.input && !this.disabled && !this.readonlyInput) {
        if (this.$refs.input.$refs.input) {
          this.$refs.input.$refs.input.onInput({
            target: {
              value: date,
            },
          })
        }
      }
    },
    handleFormatDate(val, updateFullDate = true) {
      const input = !val ? new Date() : val
      this.date = formatDate(input)
      if (this.type === 'datetime') {
        this.hours = getHoursOfDate(input)
        this.minutes = getMinutesOfDate(input)
      } else {
        this.hours = null
        this.minutes = null
      }
      if (updateFullDate) {
        this.fullDate = !val ? null : formatDate(val, this.formatComputed)
      }
    },
    onChangeInput(val) {
      if (this.fullDate !== val) {
        this.fullDate = val
        this.triggerUpdateDate(val)
      }
    },
    onChangeHours(event) {
      const val = event.target.value
      if (val === null || val === '') {
        event.target.value = '00'
        this.hours = 0
      }
    },
    onChangeMinutes(event) {
      const val = event.target.value
      if (val === null || val === '') {
        event.target.value = '00'
        this.minutes = 0
      }
    },
    onInputHours(event) {
      const val = event.target.value
      let hours = this.hours
      if (hours === val) {
        return
      }
      if (val === '') {
        this.hours = 0
        this.oldHours = 0
        return
      }
      const newValue = parseInt(val, 10)
      if (!isNaN(newValue)) {
        if (newValue > 23 || newValue < 0) {
          hours = this.oldHours
          event.target.value = hours
        } else {
          hours = newValue
        }
      } else {
        hours = this.oldHours
        event.target.value = hours
      }
      this.oldHours = hours
      this.hours = hours
      this.formatFullDate()
    },
    onInputMinutes(event) {
      const val = event.target.value
      let minutes = this.minutes
      if (minutes === val) {
        return
      }
      if (val === '') {
        this.minutes = 0
        this.oldMinutes = 0
        return
      }
      const newValue = parseInt(val, 10)
      if (!isNaN(newValue)) {
        if (newValue > 59 || newValue < 0) {
          minutes = this.oldMinutes
          event.target.value = minutes
        } else {
          minutes = newValue
        }
      } else {
        minutes = this.oldMinutes
        event.target.value = minutes
      }
      this.oldMinutes = minutes
      this.minutes = minutes
      this.formatFullDate()
    },
    onUpdateInput(val) {
      if (this.fullDate !== val) {
        this.fullDate = val
        this.triggerUpdateDate(val)

        if (val.length === 0) {
          this.handleFormatDate(null, false)
        } else if (checkDateFormat(val, this.type)) {
          this.handleFormatDate(new Date(val), false)
        }
      }
    },
    formatFullDate() {
      if (this.type === 'datetime') {
        this.fullDate = formatDate(
          setHourAndMinute(this.date, this.hours, this.minutes),
          this.formatComputed
        )
      } else {
        this.fullDate = formatDate(this.date, this.formatComputed)
      }
    },
    handleConfirmOk() {
      this.$refs.datePicker.closePopup()
      this.formatFullDate()
    },
    moveToday() {
      this.handleFormatDate(new Date())
    },
    handleKeyDown(event) {
      const keyCode = event.keyCode
      const input = event.target
      const value = input.value
      const { selectionStart, selectionEnd } = input
      const maxLength = input.maxLength
      const isNumber =
        (keyCode >= KEYCODE.NUMBER_ZERO && keyCode <= KEYCODE.NUMBER_NINE) ||
        (keyCode >= KEYCODE.NUM_LOCK_ZERO && keyCode <= KEYCODE.NUM_LOCK_NINE)
      const isArrow =
        keyCode === KEYCODE.ARROW_LEFT || keyCode === KEYCODE.ARROW_RIGHT

      if (event.shiftKey) {
        event.preventDefault()
        return false
      }

      if (
        keyCode === KEYCODE.BACKSPACE ||
        keyCode === KEYCODE.DELETE ||
        isArrow
      ) {
        return true
      }
      if (
        isNumber &&
        (selectionStart < maxLength || selectionEnd < maxLength)
      ) {
        if (
          (selectionStart === 4 || selectionStart === 7) &&
          value.length < maxLength
        ) {
          input.value =
            value.slice(0, selectionStart) + '-' + value.slice(selectionStart)
        }

        if (selectionStart === 10 && value.length < maxLength) {
          input.value =
            value.slice(0, selectionStart) + ' ' + value.slice(selectionStart)
        }

        if (selectionStart === 13 && value.length < maxLength) {
          input.value =
            value.slice(0, selectionStart) + ':' + value.slice(selectionStart)
        }

        return true
      }
      event.preventDefault()
      return false
    },
    handleClick(event) {
      const datePicker = this.$refs.datePicker
      const popup = datePicker.$refs.popup
      popup.$el.addEventListener('click', this.handleClickPickerPopup)
    },
    handleClickPickerPopup(event) {
      event.stopPropagation()
    },
    handleCalc(event) {
      this.top = event.clientY + 17 // 17 is hafl height input
    },
    handlePickNew() {
      this.formatFullDate()
    },
    clearInput() {
      this.handleFormatDate(null)
    },
    clickUpHour() {
      const hours = Number(this.hours || 0) + 1
      this.hours = hours >= 24 ? hours - 24 : hours
      this.formatFullDate()
    },
    clickDownHour() {
      const hours = Number(this.hours || 0) - 1
      this.hours = hours < 0 ? hours + 24 : hours
      this.formatFullDate()
    },
    clickUpMinutes() {
      const step = 10
      let minutes = Number(this.minutes || 0)
      if (minutes % step !== 0) {
        minutes = minutes - (minutes % step)
      }
      minutes = minutes + step
      this.minutes = minutes >= 60 ? minutes - 60 : minutes
      this.formatFullDate()
    },
    clickDownMinutes() {
      const step = 10
      let minutes = Number(this.minutes || 0)
      if (minutes % step !== 0) {
        minutes = minutes - (minutes % step)
      } else {
        minutes = minutes - step
      }
      this.minutes = minutes < 0 ? minutes + 60 : minutes
      this.formatFullDate()
    },
    disabledDate(date, currentValue) {
      const formatDateString = this.formatComputed
      if (this.disabledAfter != null) {
        const disabledAfter = new Date(
          formatDate(this.disabledAfter, formatDateString)
        )
        if (disabledAfter.getTime() <= date.getTime()) {
          return true
        }
      }
      if (this.disabledBefore != null) {
        const disabledBefore = new Date(
          formatDate(this.disabledBefore, formatDateString)
        )
        if (disabledBefore.getTime() >= date.getTime()) {
          return true
        }
      }
      return false
    },
  },
}
</script>

<template>
  <div id="base-date-picker-wrapper" class="d-flex">
    <DatePicker
      id="base-date-picker"
      ref="datePicker"
      v-model="date"
      value-type="format"
      :type="type"
      :clearable="false"
      :show-time-panel="false"
      :format="format"
      :disabled="disabled"
      :disabled-date="disabledDate"
      @pick="handlePickNew"
      @click.native="handleClick"
    >
      <template v-slot:icon-calendar>
        <div />
      </template>
      <template v-slot:input>
        <BaseInput
          ref="input"
          :value="fullDate"
          :readonly="readonlyInput"
          :vee-name="veeName"
          :maxlength="type === 'datetime' ? 16 : 10"
          :rules="{ dateFormat: type, ...rules }"
          :plaintext="disabled"
          @keydown="handleKeyDown"
          @update:value="onUpdateInput"
          @change="onChangeInput"
        >
          <template v-slot:append-content>
            <font-awesome-icon icon="calendar-alt" />
          </template>
        </BaseInput>
      </template>
      <template v-slot:footer>
        <div
          v-if="type === 'datetime'"
          id="base-date-picker-time-footer"
          class="d-flex mb-2 justify-content-center align-items-center"
        >
          <span class="mr-2">{{ $t('common.time_input') }}</span>
          <div
            class="d-flex justify-content-center align-items-center position-relative"
          >
            <input
              :value="hours"
              type="number"
              :min="0"
              :max="23"
              class="time-input form-control"
              @click="(e) => e.stopPropagation()"
              @input="onInputHours"
              @change="onChangeHours"
            />
            <div class="container-btn-step position-absolute">
              <CButton class="btn-step" @click="clickUpHour">
                <simple-line-icons class="btn-step" icon="arrow-up" no-svg />
              </CButton>
              <CButton class="btn-step" @click="clickDownHour">
                <simple-line-icons class="btn-step" icon="arrow-down" no-svg />
              </CButton>
            </div>
          </div>
          <span class="mx-1">:</span>
          <div
            class="d-flex justify-content-center align-items-center position-relative"
          >
            <input
              :value="minutes"
              type="number"
              :min="0"
              :max="59"
              class="time-input form-control"
              @input="onInputMinutes"
              @change="onChangeMinutes"
            />
            <div class="container-btn-step position-absolute">
              <CButton class="btn-step" @click="clickUpMinutes">
                <simple-line-icons class="btn-step" icon="arrow-up" no-svg />
              </CButton>
              <CButton class="btn-step" @click="clickDownMinutes">
                <simple-line-icons class="btn-step" icon="arrow-down" no-svg />
              </CButton>
            </div>
          </div>
        </div>
        <div class="d-flex base-date-picker-custom-footer">
          <CButton
            v-if="!hideClear"
            color="primary"
            variant="ghost"
            @click="clearInput"
          >
            {{ $t('common.clear') }}
          </CButton>
          <CButton
            v-if="!hideToday"
            color="primary"
            variant="ghost"
            class="mx-1"
            @click="moveToday"
          >
            {{ $t('common.today') }}
          </CButton>
          <CButton color="primary" variant="ghost" @click="handleConfirmOk">
            {{ $t('common.ok') }}
          </CButton>
        </div>
      </template>
    </DatePicker>
  </div>
</template>

<style lang="scss" scoped>
#base-date-picker {
  ::v-deep .form-group {
    margin-bottom: 0.5rem;
  }
}

.base-date-picker-custom-footer {
  > button {
    flex: 1 0 auto;
  }
}

#base-date-picker-time-footer {
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  input[type='number'] {
    -moz-appearance: textfield; /* Firefox */
  }

  .time-input {
    width: 52px;
    height: 24px;
    padding: 0 14px 0 0 !important;
    overflow: hidden;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
    border: 1px solid #ccc;
    border-radius: 3px;
    outline: 0;
    transition: border-color ease-in-out 0.2s, box-shadow ease-in-out 0.2s;
    appearance: textfield;
  }

  .custom-btn-link {
    &:focus {
      box-shadow: none;
    }
  }

  .container-btn-step {
    right: 2px;
    width: 13px;
    height: 24px;

    .btn-step {
      display: block;
      width: auto;
      height: 12px;
      padding: 0;
      margin: 0;
      font-size: 12px;
      line-height: 1;
      cursor: pointer;
    }
  }
}
</style>

<style lang="scss">
#base-date-picker-wrapper {
  .form-control[readonly] {
    display: block;
    width: 1%;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    color: #768192;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid;
    border-color: #c8ced3;
    border-radius: 0.25rem;
  }
}
</style>
